import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  IdentificationCard, SignOut, Question, BookOpen, List, X, Rocket,
} from "@phosphor-icons/react";
import NotificationsBell from "./NotificationsBell";
import ModuleSwitcher from "./ModuleSwitcher";
import CmdK from "./CmdK";
import DemoTour from "./DemoTour";
import FeedbackButton from "./FeedbackButton";
import { useAuth } from "../context/AuthContext";
import { useModules } from "../context/ModulesContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { api } from "../lib/api";
import {
  MODULES, ROLE_WORKSPACES, isRoleEligible, isEntitled, filterItems, moduleFromPath,
} from "../lib/moduleRegistry";

// When loaded inside the native mobile app's WebView we strip the chrome
// (sidebar, header, hamburger) and just render the page content. The mobile
// shell already provides navigation/back-button/title.
export const isEmbeddedMobile = () => {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("embed") === "mobile") {
      sessionStorage.setItem("embed_mobile", "1");
      return true;
    }
    return sessionStorage.getItem("embed_mobile") === "1";
  } catch { return false; }
};

export default function AppShell({ children, title }) {
  const { user, logout } = useAuth();
  const { active: activeModules } = useModules();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer whenever route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Deep links like /app/company-settings#bank scroll once the section has rendered
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); clearInterval(t); }
      else if (++tries > 15) clearInterval(t);
    }, 300);
    return () => clearInterval(t);
  }, [location.pathname, location.hash]);
  // Lock body scroll while drawer is open on mobile
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  if (!user) return null;

  // When embedded inside the native mobile WebView, render content-only.
  if (isEmbeddedMobile()) {
    return (
      <div className="min-h-screen bg-zinc-100" data-testid="app-shell-embed">
        <div className="p-3 sm:p-5">{children}</div>
      </div>
    );
  }

  const HR_ROLES = ["company_admin", "country_head", "region_head"];
  const isHr = HR_ROLES.includes(user.role);

  let nav = [];
  let currentModule = null;
  if (isHr) {
    const modId = moduleFromPath(location.pathname);
    currentModule =
      MODULES.find(m => m.id === modId && isRoleEligible(m, user.role) && isEntitled(m, activeModules) && !m.locked)
      || MODULES.find(m => m.id === "people");
    nav = filterItems(currentModule?.items || [], user.role, activeModules);
  } else {
    const ws = ROLE_WORKSPACES[user.role] || ROLE_WORKSPACES.employee;
    nav = filterItems(ws, user.role, activeModules);
  }

  const initials = (user.name || user.email || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const Sidebar = (
    <aside
      className="bg-zinc-50 border-r border-zinc-200 flex flex-col w-[260px] h-full"
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2" data-testid="brand-mark">
          <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center rounded-sm">
            <IdentificationCard weight="fill" size={18} />
          </div>
          <div>
            <div className="font-display font-black text-lg leading-none">SaarOne</div>
            <div className="tiny-label mt-1">Simply. Everything.</div>
          </div>
        </div>
        {/* Close button only visible on mobile drawer */}
        <button
          className="lg:hidden p-1.5 rounded-md text-zinc-500 hover:bg-zinc-200"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-close"
          aria-label="Close menu"
        >
          <X size={18} weight="bold"/>
        </button>
      </div>
      {isHr && currentModule && (
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2.5" data-testid="sidebar-module-label">
          <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${currentModule.color}`}>
            <currentModule.icon size={14} weight="fill"/>
          </span>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold tracking-wider text-zinc-500 uppercase">Module</div>
            <div className="text-sm font-semibold truncate">{currentModule.label}</div>
          </div>
        </div>
      )}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-zinc-950 text-white"
                  : "text-zinc-700 hover:bg-zinc-100"
              }`
            }
          >
            <item.icon size={18} weight="regular" />
            <span>{item.label}</span>
          </NavLink>
        ))}
        <div className="pt-3 mt-3 border-t border-zinc-100">
          <NavLink
            to="/app/help"
            data-testid="nav-help"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`
            }
          >
            <BookOpen size={18} weight="regular" />
            <span>Help & Knowledge Base</span>
          </NavLink>
        </div>
      </nav>
      <div className="px-3 py-3 border-t border-zinc-200">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-9 w-9 border border-zinc-200">
            <AvatarFallback className="bg-zinc-950 text-white text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user.role.replace(/_/g, " ")}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
          >
            <SignOut size={18} />
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex bg-zinc-100" data-testid="app-shell">
      {/* Desktop sidebar — always visible at lg+ */}
      <div className="hidden lg:block flex-none">
        {Sidebar}
      </div>

      {/* Mobile drawer — backdrop + slide-in */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-zinc-950/60 z-40 lg:hidden"
            onClick={() => setDrawerOpen(false)}
            data-testid="drawer-backdrop"
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden shadow-2xl animate-in slide-in-from-left duration-200">
            {Sidebar}
          </div>
        </>
      )}

      <main className="overflow-y-auto flex-1 min-w-0">
        <header className="bg-white border-b border-zinc-200 px-3 sm:px-4 lg:px-8 py-3 sticky top-0 z-30 flex items-center justify-between gap-2" data-testid="top-header">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {/* Hamburger — mobile only */}
            <button
              type="button"
              className="lg:hidden p-2 -ml-1 rounded-md text-zinc-700 hover:bg-zinc-100 flex-none"
              onClick={() => setDrawerOpen(true)}
              data-testid="drawer-open"
              aria-label="Open menu"
            >
              <List size={22} weight="bold"/>
            </button>
            {isHr && <ModuleSwitcher/>}
            {currentModule && (
              <div className="h-6 w-px bg-zinc-200 mx-1 hidden md:block"/>
            )}
            <div className="min-w-0">
              {currentModule && (
                <div className="tiny-label hidden sm:block">
                  {currentModule.label}{title && " ·"}
                </div>
              )}
              {!currentModule && (
                <div className="tiny-label hidden sm:block">{user.role.replace(/_/g, " ")}</div>
              )}
              <h1 className="font-display font-bold text-base sm:text-xl leading-tight tracking-tight truncate" data-testid="page-title">
                {title}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <SetupBadge/>
            <div className="hidden sm:block"><CmdK/></div>
            <NotificationsBell/>
            <NavLink
              to="/app/help"
              className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-950"
              data-testid="header-help-link"
            >
              <Question size={14} weight="bold"/>
              <span className="hidden sm:inline">Help</span>
            </NavLink>
          </div>
        </header>
        {user.must_change_password && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3" data-testid="must-change-password-banner">
            <div className="text-[13px] text-amber-900">
              <strong>Action needed —</strong> you're still signed in with a temporary password. Please change it now from{" "}
              <NavLink to="/app/me?tab=security" className="underline font-medium hover:text-amber-950">My Profile → Security</NavLink>.
            </div>
          </div>
        )}
        <div className="p-3 sm:p-5 lg:p-8">{children}</div>
      </main>
      {(["super_admin","company_admin","country_head","region_head","branch_manager"].includes(user.role)) && <DemoTour/>}
      <FeedbackButton />
    </div>
  );
}

export function StatCard({ label, value, hint, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 sm:p-6" data-testid={testid}>
      <div className="tiny-label">{label}</div>
      <div className="font-display font-bold text-3xl sm:text-4xl mt-2 sm:mt-3 tracking-tight" data-testid={`${testid}-value`}>{value}</div>
      {hint && <div className="text-xs sm:text-sm text-zinc-500 mt-1.5 sm:mt-2">{hint}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SetupBadge — header pill showing "Finish setup (8/22) →" until complete.
// Auto-fetches /api/setup/progress; gracefully hides on 403 / non-admin roles.
// ─────────────────────────────────────────────────────────────────────────────
function SetupBadge() {
  const { user } = useAuth();
  const location = useLocation();
  const [p, setP] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("setup:progress") || "null"); } catch { return null; }
  });
  const isAdmin = ["super_admin", "company_admin", "country_head", "region_head"].includes(user?.role);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api.get("/setup/progress", { silent: true })
      .then(r => { if (!cancelled) { setP(r.data); sessionStorage.setItem("setup:progress", JSON.stringify(r.data)); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, location.pathname]);  // re-fetch on route change so auto-detection updates

  if (!isAdmin || !p) return null;
  if (p.is_complete) return null;
  if (p.dismissed_until && new Date(p.dismissed_until) > new Date()) return null;
  if (location.pathname.startsWith("/app/setup")) return null;   // hide on the setup page itself

  return (
    <Link
      to="/app/setup"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors shrink-0"
      data-testid="setup-badge"
      title={`${p.done} of ${p.total} setup steps complete`}
    >
      <Rocket size={12} weight="fill" className="text-amber-600"/>
      <span className="hidden sm:inline">Finish setup</span>
      <span className="font-mono font-bold">{p.done}/{p.total}</span>
      <span className="w-8 h-1 bg-amber-200 rounded-full overflow-hidden hidden sm:inline-block">
        <span className="block h-full bg-amber-500" style={{ width: `${p.progress}%` }}/>
      </span>
    </Link>
  );
}

export function SectionCard({ title, subtitle, action, right, children, testid }) {
  action = action ?? right;
  return (
    <section className="bg-white border border-zinc-200 rounded-lg" data-testid={testid}>
      <header className="px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-200 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm sm:text-base">{title}</div>
          {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
        </div>
        {action ? <div className="flex-none w-full sm:w-auto">{action}</div> : null}
      </header>
      <div className="p-4 sm:p-6 overflow-x-auto">{children}</div>
    </section>
  );
}
